import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { 
  Building2, 
  Home, 
  Droplets, 
  Ship, 
  CheckCircle2, 
  Phone, 
  Mail, 
  MapPin,
  Menu,
  X,
  ChevronRight,
  Award,
  Clock,
  Shield,
  Users,
  Lightbulb,
  Target,
  Facebook,
  Linkedin,
  Twitter,
  Instagram,
  Star,
  Heart,
  ArrowRight,
  PhoneCall,
  Briefcase,
  HardHat,
  Ruler,
  Globe
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { useForm, ValidationError } from '@formspree/react';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_e1826640-30f1-4d2e-8b34-eb47fc069a52/artifacts/rnwr0xhm_Logo%20Elite%20Africa.png";

// Images fournies par le client - DIVERSIFIÉES
const IMAGES = {
  // Services - images client
  construction: "https://customer-assets.emergentagent.com/job_elite-sarl-benin/artifacts/1gfypf38_construction%20et%20G%C3%A9nie%20Civil.png",
  immobilier: "https://customer-assets.emergentagent.com/job_elite-sarl-benin/artifacts/b85f69d0_promotion%20immobil%C3%A8re.png",
  aep: "https://customer-assets.emergentagent.com/job_elite-sarl-benin/artifacts/pwiy6utv_Adduction%20d%27eau.png",
  importExport: "https://customer-assets.emergentagent.com/job_elite-sarl-benin/artifacts/akfdtsqz_Import%20export.png",
  bureau: "https://customer-assets.emergentagent.com/job_elite-sarl-benin/artifacts/z0ebxied_Salutations%20dans%20un%20bureau%20moderne.png",
  
  // Nouvelle image pour l'équipe de professionnels
  equipe: "https://customer-assets.emergentagent.com/job_elite-sarl-benin/artifacts/r1qf0wpr_promotion%20immobil%C3%A8re.png",
  
  // Images supplémentaires pour diversifier
  constructionAlt: "https://customer-assets.emergentagent.com/job_elite-sarl-benin/artifacts/icryluge_chantier%20de%20construction.png",
  immobilierAlt: "https://images.unsplash.com/photo-1771457362598-eb92f9f4bf42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGFmcmljYSUyMHJlYWwlMjBlc3RhdGV8ZW58MHx8fHwxNzc1NjQ5OTE0fDA&ixlib=rb-4.1.0&q=85",
  portAlt: "https://images.unsplash.com/photo-1719269220476-f94ae5fc26e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXBwaW5nJTIwY29udGFpbmVycyUyMHBvcnQlMjBhZnJpY2ElMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzc1NjQ5OTE0fDA&ixlib=rb-4.1.0&q=85",
  warehouse: "https://images.unsplash.com/photo-1769752803940-0acb89683123?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHNoaXBwaW5nJTIwY29udGFpbmVycyUyMHBvcnQlMjBhZnJpY2ElMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzc1NjQ5OTE0fDA&ixlib=rb-4.1.0&q=85"
};

// Contact info
const CONTACT_INFO = {
  phone: "+229 01 94 11 77 77",
  email: "contact@eliteafricasarl.com",
  address: "Cotonou, Bénin",
  facebook: "https://www.facebook.com/profile.php?id=61573325793624",
  linkedin: "https://www.linkedin.com/company/elite-africa-sarl",
  instagram: "https://www.instagram.com/eliteafricasarl/"
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "", variants = fadeInUp }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  const whatsappLink = `https://wa.me/${CONTACT_INFO.whatsapp.replace(/\s/g, '')}`;
  
  return (
    <motion.a
      href={whatsappLink}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-12 h-12 sm:w-14 sm:h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform duration-300"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 1.5, type: "spring", stiffness: 200 }}
      data-testid="whatsapp-button"
      aria-label="Contactez-nous sur WhatsApp"
    >
      <svg viewBox="0 0 24 24" className="w-6 h-6 sm:w-7 sm:h-7 text-white fill-current">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </motion.a>
  );
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#accueil", label: "Accueil" },
    { href: "#apropos", label: "À propos" },
    { href: "#services", label: "Services" },
    { href: "#projets", label: "Projets" },
    { href: "#expertise", label: "Expertise" },
    { href: "#contact", label: "Contact" }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    
    // Fermer le menu mobile d'abord
    setIsMobileMenuOpen(false);
    
    // Petit délai pour laisser le menu se fermer
    setTimeout(() => {
      // Mettre à jour l'URL sans le dièse
      const path = href.replace('#', '/');
      window.history.pushState(null, '', path === '/accueil' ? '/' : path);
      
      if (href === "#accueil") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const element = document.querySelector(href);
        if (element) {
          const navHeight = 100;
          const elementTop = element.offsetTop - navHeight;
          window.scrollTo({ top: elementTop, behavior: "smooth" });
        }
      }
    }, 100);
  };

  return (
    <nav 
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "bg-white shadow-lg py-2" : "bg-white py-3 sm:py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo - AGRANDI */}
          <a href="#accueil" onClick={(e) => scrollToSection(e, "#accueil")} className="flex items-center gap-3 group">
            <img src={LOGO_URL} alt="Elite Africa" className="h-16 sm:h-20 w-auto transition-transform duration-300 group-hover:scale-105" data-testid="logo" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                data-testid={`nav-${link.href.replace('#', '')}`}
                className="text-sm font-semibold text-slate-600 hover:text-blue-900 transition-all duration-300 relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-900 group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t shadow-2xl"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="block text-slate-700 hover:text-blue-900 hover:bg-blue-50 font-semibold py-3 px-4 rounded-xl transition-all text-sm"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Section - Clean White Design - SANS LOGO DUPLIQUÉ
const HeroSection = () => {
  return (
    <section id="accueil" data-testid="hero-section" className="relative min-h-screen flex items-center bg-white pt-20 sm:pt-24 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
        <div className="text-center max-w-4xl mx-auto">
          {/* Nom de l'entreprise - SANS LOGO ICI */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-6 sm:mb-8"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight" data-testid="hero-title">
              <span className="text-blue-900">Elite</span>
              <span className="text-slate-300 font-light mx-2 sm:mx-3">|</span>
              <span className="text-blue-900">Africa</span>
              <span className="block text-xl sm:text-2xl lg:text-3xl font-light text-slate-400 mt-2 tracking-[0.2em] sm:tracking-[0.3em]">
                SARL
              </span>
            </h1>
          </motion.div>

          {/* Slogan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-4 sm:mb-6"
          >
            <p className="text-xl sm:text-2xl lg:text-3xl font-light text-slate-600 italic px-4">
              Construire l'excellence, bâtir l'avenir
            </p>
          </motion.div>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-base sm:text-lg text-slate-500 mb-8 sm:mb-10 max-w-xl mx-auto leading-relaxed px-4"
            data-testid="hero-subtitle"
          >
            Votre partenaire de confiance pour des projets de construction, 
            d'infrastructures et de développement immobilier au Bénin.
          </motion.p>

          {/* Boutons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex justify-center mb-10 sm:mb-12 px-4"
          >
            <Button
              data-testid="hero-services-button"
              onClick={() => document.querySelector("#services").scrollIntoView({ behavior: "smooth" })}
              className="group bg-blue-900 text-white hover:bg-blue-800 px-8 sm:px-10 py-5 sm:py-6 rounded-full font-semibold text-sm sm:text-base shadow-xl hover:shadow-2xl transition-all duration-300"
            >
              Découvrir nos services
              <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>

          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-wrap justify-center gap-6 sm:gap-10 pt-6 sm:pt-8 border-t border-slate-200 mx-4"
          >
            {[
              { value: "2023", label: "Année de création" },
              { value: "50+", label: "Projets réalisés" },
              { value: "100%", label: "Engagement qualité" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl sm:text-3xl font-black text-blue-900">{stat.value}</div>
                <div className="text-slate-500 text-xs sm:text-sm font-medium mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Section - OPTIMISÉ MOBILE
const AboutSection = () => {
  return (
    <section id="apropos" data-testid="about-section" className="py-16 sm:py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Image */}
          <AnimatedSection variants={fadeInLeft}>
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl">
              <img 
                src={IMAGES.bureau} 
                alt="Équipe Elite Africa" 
                className="w-full h-64 sm:h-80 md:h-96 lg:h-[450px] object-cover"
              />
            </div>
          </AnimatedSection>

          {/* Content */}
          <AnimatedSection variants={fadeInRight}>
            <span className="inline-block text-blue-900 text-xs sm:text-sm uppercase tracking-[0.2em] font-bold mb-3 sm:mb-4">
              À propos de nous
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight leading-tight" data-testid="about-title">
              Une entreprise <span className="text-blue-900">dynamique</span> au service du développement
            </h2>
            <div className="w-16 h-1 bg-blue-900 rounded-full mb-6 sm:mb-8" />
            
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">
              <strong className="text-slate-800">Elite Africa SARL</strong> est une entreprise béninoise dynamique, spécialisée dans les travaux publics et privés, l'immobilier, les projets d'adduction d'eau potable et le commerce international.
            </p>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-6 sm:mb-8">
              Créée en 2023, notre mission est de contribuer activement au développement du Bénin en offrant des solutions de qualité, adaptées aux besoins de nos clients.
            </p>

            {/* Key points */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {[
                { icon: Award, text: "Équipe compétente" },
                { icon: Users, text: "Approche personnalisée" },
                { icon: Shield, text: "Travail rigoureux" },
                { icon: Target, text: "Vision long terme" }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 sm:p-4 bg-white rounded-xl shadow-sm"
                >
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-blue-900 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <span className="text-slate-700 font-semibold text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Services Section - IMAGES DIVERSIFIÉES + MOBILE OPTIMISÉ
const ServicesSection = () => {
  const services = [
    {
      icon: Building2,
      title: "Construction & génie civil",
      description: "Réalisation de projets de construction de haute qualité : bâtiments résidentiels, commerciaux, industriels et infrastructures.",
      image: IMAGES.construction
    },
    {
      icon: Home,
      title: "Promotion immobilière",
      description: "Développement de programmes immobiliers modernes et accessibles. Nous créons des espaces de vie adaptés à vos besoins.",
      image: IMAGES.immobilier
    },
    {
      icon: Droplets,
      title: "Adduction d'eau potable",
      description: "Conception et réalisation de réseaux d'adduction d'eau potable pour les communautés urbaines et rurales.",
      image: IMAGES.aep
    },
    {
      icon: Ship,
      title: "Import-export & commerce",
      description: "Services de commerce international et d'import-export. Nous facilitons les échanges entre le Bénin et le monde.",
      image: IMAGES.importExport
    }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-16 sm:py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <AnimatedSection className="text-center mb-12 sm:mb-16">
          <span className="inline-block text-blue-900 text-xs sm:text-sm uppercase tracking-[0.2em] font-bold mb-3 sm:mb-4">
            Nos services
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight px-4" data-testid="services-title">
            Des solutions <span className="text-blue-900">complètes</span> pour vos projets
          </h2>
          <div className="w-16 h-1 bg-blue-900 rounded-full mx-auto mb-4 sm:mb-6" />
          <p className="text-slate-600 text-sm sm:text-base max-w-xl mx-auto px-4">
            De la construction à l'import-export, nous offrons une gamme complète de services.
          </p>
        </AnimatedSection>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="group bg-white border border-slate-100 rounded-2xl sm:rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500"
              data-testid={`service-card-${index}`}
            >
              <div className="relative h-48 sm:h-56 lg:h-64 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
              </div>
              <div className="p-5 sm:p-6 lg:p-8">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-blue-900 flex items-center justify-center mb-4 sm:mb-5 shadow-lg">
                  <service.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">{service.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Projects Section - IMAGES VARIÉES
const ProjectsSection = () => {
  const projects = [
    {
      title: "Projet résidentiel",
      category: "Immobilier",
      description: "Construction de logements modernes",
      image: IMAGES.immobilierAlt // Image différente
    },
    {
      title: "Chantier de construction",
      category: "Construction",
      description: "Réalisation d'infrastructures",
      image: IMAGES.constructionAlt // Image différente
    },
    {
      title: "Réseau hydraulique",
      category: "Hydraulique",
      description: "Adduction d'eau potable",
      image: IMAGES.aep
    },
    {
      title: "Logistique portuaire",
      category: "Import-Export",
      description: "Commerce international",
      image: IMAGES.portAlt // Image différente
    }
  ];

  return (
    <section id="projets" data-testid="projects-section" className="py-16 sm:py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <AnimatedSection className="text-center mb-12 sm:mb-16">
          <span className="inline-block text-blue-900 text-xs sm:text-sm uppercase tracking-[0.2em] font-bold mb-3 sm:mb-4">
            Portfolio
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight px-4" data-testid="projects-title">
            Nos <span className="text-blue-900">domaines</span> d'intervention
          </h2>
          <div className="w-16 h-1 bg-blue-900 rounded-full mx-auto mb-4 sm:mb-6" />
          <p className="text-slate-600 text-sm sm:text-base max-w-xl mx-auto px-4">
            Découvrez les secteurs dans lesquels nous intervenons.
          </p>
        </AnimatedSection>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6"
        >
          {projects.map((project, index) => (
            <motion.div
              key={index}
              variants={scaleIn}
              className="group relative rounded-xl sm:rounded-2xl overflow-hidden aspect-[3/4] cursor-pointer shadow-md"
              data-testid={`project-card-${index}`}
            >
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 lg:p-5 text-white">
                <span className="inline-block text-[10px] sm:text-xs uppercase tracking-wider text-blue-300 font-bold mb-1">
                  {project.category}
                </span>
                <h3 className="text-sm sm:text-base lg:text-lg font-bold leading-tight">{project.title}</h3>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Why Choose Us Section - MOBILE OPTIMISÉ
const WhyChooseUsSection = () => {
  const reasons = [
    { icon: Award, title: "Expertise technique", description: "Équipe compétente et passionnée" },
    { icon: Clock, title: "Respect des délais", description: "Livraison dans les temps" },
    { icon: Shield, title: "Qualité", description: "Matériaux sélectionnés" },
    { icon: MapPin, title: "Connaissance locale", description: "Maîtrise du marché" },
    { icon: Users, title: "Professionnalisme", description: "Suivi rigoureux" }
  ];

  return (
    <section data-testid="why-choose-section" className="py-16 sm:py-24 md:py-32 bg-blue-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <AnimatedSection className="text-center mb-12 sm:mb-16">
          <span className="inline-block text-blue-300 text-xs sm:text-sm uppercase tracking-[0.2em] font-bold mb-3 sm:mb-4">
            Nos atouts
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-4 sm:mb-6 tracking-tight px-4" data-testid="why-choose-title">
            Pourquoi nous <span className="text-blue-300">choisir</span> ?
          </h2>
          <div className="w-16 h-1 bg-blue-400 rounded-full mx-auto" />
        </AnimatedSection>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6"
        >
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className={`bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-5 text-center ${index === 4 ? 'col-span-2 sm:col-span-1 max-w-[200px] mx-auto sm:max-w-none' : ''}`}
              data-testid={`reason-${index}`}
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-xl bg-white/20 flex items-center justify-center">
                <reason.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <h3 className="text-sm sm:text-base font-bold text-white mb-1 sm:mb-2">{reason.title}</h3>
              <p className="text-white/70 text-xs sm:text-sm leading-relaxed">{reason.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Values Section - MOBILE OPTIMISÉ
const ValuesSection = () => {
  const values = [
    { icon: Star, title: "Excellence", description: "Nous visons l'excellence dans chaque projet.", color: "bg-amber-500" },
    { icon: Lightbulb, title: "Innovation", description: "Méthodes avancées pour des résultats optimaux.", color: "bg-blue-500" },
    { icon: Shield, title: "Fiabilité", description: "Nous tenons nos promesses.", color: "bg-green-500" },
    { icon: Heart, title: "Engagement", description: "Nous nous investissons pleinement.", color: "bg-rose-500" }
  ];

  return (
    <section data-testid="values-section" className="py-16 sm:py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <AnimatedSection className="text-center mb-12 sm:mb-16">
          <span className="inline-block text-blue-900 text-xs sm:text-sm uppercase tracking-[0.2em] font-bold mb-3 sm:mb-4">
            Notre ADN
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight px-4" data-testid="values-title">
            Nos <span className="text-blue-900">valeurs</span>
          </h2>
          <div className="w-16 h-1 bg-blue-900 rounded-full mx-auto" />
        </AnimatedSection>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
        >
          {values.map((value, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-slate-50 rounded-xl sm:rounded-2xl p-5 sm:p-6 text-center"
              data-testid={`value-${index}`}
            >
              <div className={`w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-5 rounded-xl ${value.color} flex items-center justify-center shadow-lg`}>
                <value.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2 sm:mb-3">{value.title}</h3>
              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">{value.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Expertise Section - IMAGE DIFFÉRENTE
const ExpertiseSection = () => {
  const expertiseAreas = [
    { icon: HardHat, title: "Ingénieurs BTP", description: "Génie civil" },
    { icon: Ruler, title: "Techniciens", description: "Conception" },
    { icon: Droplets, title: "Hydrauliciens", description: "Adduction d'eau" },
    { icon: Globe, title: "Commerciaux", description: "Import-export" }
  ];

  return (
    <section id="expertise" data-testid="expertise-section" className="py-16 sm:py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Image - NOUVELLE IMAGE ÉQUIPE */}
          <AnimatedSection variants={fadeInLeft}>
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl">
              <img 
                src={IMAGES.equipe} 
                alt="Notre équipe de professionnels" 
                className="w-full h-64 sm:h-80 md:h-96 lg:h-[450px] object-cover"
              />
            </div>
          </AnimatedSection>

          {/* Content */}
          <AnimatedSection variants={fadeInRight}>
            <span className="inline-block text-blue-900 text-xs sm:text-sm uppercase tracking-[0.2em] font-bold mb-3 sm:mb-4">
              Notre expertise
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight leading-tight" data-testid="expertise-title">
              Une équipe de <span className="text-blue-900">professionnels</span>
            </h2>
            <div className="w-16 h-1 bg-blue-900 rounded-full mb-6 sm:mb-8" />
            
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">
              Notre équipe est composée de professionnels qualifiés et expérimentés, engagés dans la réalisation de projets durables et de qualité.
            </p>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-6 sm:mb-8">
              Ingénieurs, techniciens et experts travaillent ensemble pour garantir le succès de chaque projet.
            </p>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {expertiseAreas.map((area, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 sm:p-4 bg-white rounded-xl shadow-sm"
                >
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-blue-900 flex items-center justify-center flex-shrink-0">
                    <area.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">{area.title}</h4>
                    <p className="text-slate-500 text-xs">{area.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Contact Section - FORMSPREE REACT
const ContactSection = () => {
  const [state, handleSubmit] = useForm('meepddre');

  const contactMethods = [
    { icon: MapPin, label: "Adresse", value: CONTACT_INFO.address },
    { icon: Phone, label: "Téléphone", value: CONTACT_INFO.phone, action: `tel:${CONTACT_INFO.phone.replace(/\s/g, '')}` },
    { icon: Mail, label: "Email", value: CONTACT_INFO.email, action: `mailto:${CONTACT_INFO.email}` }
  ];

  return (
    <section id="contact" data-testid="contact-section" className="py-16 sm:py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <AnimatedSection className="text-center mb-12 sm:mb-16">
          <span className="inline-block text-blue-900 text-xs sm:text-sm uppercase tracking-[0.2em] font-bold mb-3 sm:mb-4">
            Contact
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight px-4" data-testid="contact-title">
            Parlons de votre <span className="text-blue-900">projet</span>
          </h2>
          <div className="w-16 h-1 bg-blue-900 rounded-full mx-auto" />
        </AnimatedSection>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          {/* Contact Info */}
          <AnimatedSection variants={fadeInLeft}>
            <div className="bg-blue-900 rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-white h-full">
              <h3 className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8">Nos coordonnées</h3>
              
              <div className="space-y-4 sm:space-y-6 mb-8">
                {contactMethods.map((info, index) => (
                  <div key={index} data-testid={`contact-info-${index}`}>
                    {info.action ? (
                      <a href={info.action} className="flex items-center gap-4 p-3 sm:p-4 rounded-xl hover:bg-white/10 transition-colors">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <info.icon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-300" />
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-blue-300">{info.label}</p>
                          <p className="font-semibold text-sm sm:text-base">{info.value}</p>
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-center gap-4 p-3 sm:p-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <info.icon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-300" />
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-blue-300">{info.label}</p>
                          <p className="font-semibold text-sm sm:text-base">{info.value}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Call button */}
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`}
                className="flex items-center justify-center gap-2 w-full bg-white text-blue-900 font-bold py-3 sm:py-4 px-6 rounded-xl transition-all duration-300 text-sm sm:text-base"
                data-testid="contact-call-button"
              >
                <PhoneCall className="w-5 h-5" />
                Appeler maintenant
              </a>

              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-xs sm:text-sm text-blue-300 mb-3">Suivez-nous</p>
                <div className="flex gap-2 sm:gap-3">
                  <a
                    href={CONTACT_INFO.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-all"
                    data-testid="social-link-facebook"
                  >
                    <Facebook className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </a>
                  <a
                    href={CONTACT_INFO.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-all"
                    data-testid="social-link-linkedin"
                  >
                    <Linkedin className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </a>
                  <a
                    href={CONTACT_INFO.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-all"
                    data-testid="social-link-instagram"
                  >
                    <Instagram className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Contact Form */}
          <AnimatedSection variants={fadeInRight}>
            {state.succeeded ? (
              <div className="bg-green-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center h-full flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-green-800 mb-2">Message envoyé !</h3>
                <p className="text-green-600">Nous vous répondrons dans les plus brefs délais.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8" data-testid="contact-form">
                <input type="hidden" name="_subject" value="Nouveau message depuis le site Elite Africa" />
                <div className="space-y-4 sm:space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                        Nom complet
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Votre nom"
                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 transition-colors bg-white text-sm"
                        data-testid="contact-name-input"
                        required
                      />
                      <ValidationError prefix="Nom" field="name" errors={state.errors} className="text-red-500 text-xs mt-1" />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                        Téléphone
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+229 XX XX XX XX"
                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 transition-colors bg-white text-sm"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                      Adresse email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="votre@email.com"
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 transition-colors bg-white text-sm"
                      data-testid="contact-email-input"
                      required
                    />
                    <ValidationError prefix="Email" field="email" errors={state.errors} className="text-red-500 text-xs mt-1" />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                      Votre message
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Décrivez votre projet..."
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 transition-colors resize-none bg-white text-sm"
                      data-testid="contact-message-input"
                      required
                    />
                    <ValidationError prefix="Message" field="message" errors={state.errors} className="text-red-500 text-xs mt-1" />
                  </div>
                  <Button
                    type="submit"
                    disabled={state.submitting}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white py-4 sm:py-5 rounded-xl font-bold text-sm sm:text-base shadow-lg disabled:opacity-70"
                    data-testid="contact-submit-button"
                  >
                    {state.submitting ? (
                      <span>Envoi en cours...</span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Envoyer le message
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Footer - OPTIMISÉ MOBILE
const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const quickLinks = [
    { label: "Accueil", href: "#accueil" },
    { label: "À propos", href: "#apropos" },
    { label: "Services", href: "#services" },
    { label: "Contact", href: "#contact" }
  ];

  const services = [
    "Construction",
    "Immobilier", 
    "Adduction d'eau",
    "Import-export"
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    // Mettre à jour l'URL sans le dièse
    const path = href.replace('#', '/');
    window.history.pushState(null, '', path === '/accueil' ? '/' : path);
    
    if (href === "#accueil") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const element = document.querySelector(href);
      if (element) {
        const navHeight = 100; // Hauteur de la navbar
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: elementPosition - navHeight, behavior: "smooth" });
      }
    }
  };

  return (
    <footer data-testid="footer" className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <img src={LOGO_URL} alt="Elite Africa" className="h-12 sm:h-14 w-auto mb-4" />
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Votre partenaire pour la construction et le commerce au Bénin.
            </p>
            <div className="flex gap-2">
              <a
                href={CONTACT_INFO.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-900 transition-all"
                data-testid="footer-social-facebook"
              >
                <Facebook className="w-4 h-4 text-slate-400" />
              </a>
              <a
                href={CONTACT_INFO.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-900 transition-all"
                data-testid="footer-social-linkedin"
              >
                <Linkedin className="w-4 h-4 text-slate-400" />
              </a>
              <a
                href={CONTACT_INFO.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-900 transition-all"
                data-testid="footer-social-instagram"
              >
                <Instagram className="w-4 h-4 text-slate-400" />
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-sm font-bold mb-4">Navigation</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-1"
                    data-testid={`footer-link-${index}`}
                  >
                    <ChevronRight className="w-3 h-3" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-bold mb-4">Services</h4>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={index} className="text-slate-400 text-sm flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-500 rounded-full" />
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-bold mb-4">Contact</h4>
            <ul className="space-y-3 text-slate-400 text-sm">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <span>{CONTACT_INFO.address}</span>
              </li>
              <li>
                <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-white">
                  <Phone className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <span>{CONTACT_INFO.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-2 hover:text-white">
                  <Mail className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <span className="break-all">{CONTACT_INFO.email}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-5">
          <p className="text-slate-500 text-xs sm:text-sm text-center">
            © {currentYear} <strong>Elite Africa SARL</strong>. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="App" data-testid="app">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <ProjectsSection />
      <WhyChooseUsSection />
      <ValuesSection />
      <ExpertiseSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
